import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const HarKiDun = () => {
  const tourData = {
    name: "Har Ki Dun Trek",
    duration: "06 Nights 07 Days",
    price: "₹ 7,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935181/i9d3w6xabdgelkzseik6.jpg",
    altitude: "11,675 ft",
    difficulty: "Moderate",
    region: "Uttarakhand",
    highlights: ["Valley trek", "Ancient route", "Cultural villages"],
    bestTime: "March to June, September to November",
    description: "The Har Ki Dun Trek is one of the most beautiful valley treks in Uttarakhand, offering stunning views of the Himalayas and a chance to explore ancient cultural villages. This moderate trek takes you through lush green valleys, alpine meadows, and traditional mountain villages."
  };

  return (
    <>
      <Helmet>
        <title>Har Ki Dun Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Har Ki Dun Trek in Uttarakhand. Ancient valley trek with stunning mountain views and cultural village exploration." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              A beautiful valley trek through ancient cultural villages
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek follows an ancient route used by shepherds and traders, offering a perfect blend 
                    of adventure and cultural exploration. The Har Ki Dun valley is known for its pristine beauty, 
                    traditional villages, and breathtaking views of snow-capped peaks.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Ancient valley trek route</li>
                    <li>Cultural village exploration</li>
                    <li>Stunning mountain views</li>
                    <li>Alpine meadows and forests</li>
                    <li>Traditional architecture</li>
                    <li>Wildlife spotting opportunities</li>
                    <li>Photography paradise</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: From Dehradun to Sankri</h4>
                    <ul>
                      <li>Morning pick up from Dehradun (6-8 am)</li>
                      <li>240 Km, 10 hours long journey to Sankri</li>
                      <li>Route: Through Mussorie, Barkot, Purola, Mori & Netwar along the Tons River</li>
                      <li>Scenic landscapes throughout the journey</li>
                      <li>Arrive at Sankri village in Uttarkashi state</li>
                      <li>Sankri is located at 1952 meters altitude in Govind wildlife sanctuary</li>
                      <li>Starting point for famous treks like Kedarkantha, Har ki Dun, Rupin Pass, Bali Pass</li>
                      <li>Perfect place for mountain vacations and adventurous trips</li>
                      <li>Stunning views of the Himalayas</li>
                      <li>Historical and cultural sights with wooden architecture</li>
                      <li>Overnight stay in Sankri with village dinner</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: From Sankri to Gangarh Village to Osla (homestay/campsite)</h4>
                    <ul>
                      <li>Breakfast at Sankri</li>
                      <li>16 km drive from Sankri to Gangarh Village</li>
                      <li>Start of adventurous day</li>
                      <li>4 km trek from Gangarh village to Osla village</li>
                      <li>Trek duration: 3-4 hours</li>
                      <li>Pass through Himalayan region with wild orchids</li>
                      <li>Route starts in valley next to Rupin river</li>
                      <li>Path from Taluka village drops then rises</li>
                      <li>Pass by little waterfall after 20 minutes</li>
                      <li>15-minute difficult ascent to vantage point</li>
                      <li>View of two valleys separated by Karmanasha stream</li>
                      <li>Left valley: Maninda Taal and Borasu Pass</li>
                      <li>Right valley: Jaundar Glacier</li>
                      <li>Overnight stay at homestay/campsite in Osla village</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Trek from Osla to Kalkatiyadhar trek (8kms trek)</h4>
                    <ul>
                      <li>15-minute trek to first cleared lands</li>
                      <li>Gentle introduction to the journey ahead</li>
                      <li>Route climbs over meeting of Supin and Ruinsara rivers</li>
                      <li>Reach lookout point</li>
                      <li>View Kala Nag (Black Peak) and Bandarpooch mountains</li>
                      <li>Next campground: Kalkatiyadhar</li>
                      <li>Elevation: 8,956 feet</li>
                      <li>Stunning views throughout the trek</li>
                      <li>Serene camping opportunities</li>
                      <li>Total trek distance: 8 km</li>
                      <li>Memorable experience with nature</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Trek from Kalkatiyadhaar to Har-ki-dun trek & back</h4>
                    <ul>
                      <li>View Dev Thach meadows on the right</li>
                      <li>Begin scenic journey through the valley</li>
                      <li>Valley divides into two halves</li>
                      <li>Left side: Har-ki-Dun</li>
                      <li>Right side: Ruinsara Taal and Bali Pass</li>
                      <li>Round the bend in the mountain</li>
                      <li>View Har Ki Dun peak and Hata Peak</li>
                      <li>Har-ki-Dun valley below</li>
                      <li>Cross several creeks</li>
                      <li>Rhododendron trees in pine forest</li>
                      <li>Magnificent cascading stream</li>
                      <li>Himalayan alpine flowers along stream edges</li>
                      <li>Pass little waterfall after 20 minutes</li>
                      <li>15-minute difficult ascent</li>
                      <li>View two valleys separated by Karmanasha stream</li>
                      <li>Left valley: Maninda Taal and Borasu Pass</li>
                      <li>Right valley: Jaundar Glacier</li>
                      <li>Set up camp in Kalkatiyadhaar</li>
                      <li>Relax after day of exploration</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: From Kalkatiyadhaar to Osla trek</h4>
                    <ul>
                      <li>Take same path back to Osla</li>
                      <li>Simple and downward track</li>
                      <li>Less strenuous compared to previous days</li>
                      <li>Downward slope allows relaxed pace</li>
                      <li>Enjoy scenic beauty leisurely</li>
                      <li>Arrive at Osla campground</li>
                      <li>Overnight stay</li>
                      <li>Well-deserved respite after high-altitude adventure</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: From Osla to Sankri</h4>
                    <ul>
                      <li>Early breakfast</li>
                      <li>Start of descent journey</li>
                      <li>Simpler journey back</li>
                      <li>Retrace our steps</li>
                      <li>Descend 8 km to Dhatmeer village</li>
                      <li>Proceed to Gangarg village at riverside</li>
                      <li>Continue journey to Sankri</li>
                      <li>Board vehicle for Sankri</li>
                      <li>Complete trek journey</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 7: From Sankri to Dehradun</h4>
                    <ul>
                      <li>Last day of Har Ki Dun Trekking journey</li>
                      <li>Say goodbye to the mountains</li>
                      <li>Bid farewell to new friends</li>
                      <li>Take best memories home</li>
                      <li>Breakfast at Sankri</li>
                      <li>Board vehicle for journey to Dehradun</li>
                      <li>Trek comes to an end</li>
                      <li>Memories will last forever</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Backpack (40-50 liters)</li>
                        <li>Sleeping bag</li>
                        <li>Trekking poles</li>
                        <li>Headlamp with extra batteries</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Waterproof trekking shoes</li>
                        <li>Woolen socks (2-3 pairs)</li>
                        <li>Gaiters</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Thermal wear (2-3 sets)</li>
                        <li>Fleece jacket</li>
                        <li>Waterproof jacket</li>
                        <li>Quick dry t-shirts</li>
                        <li>Warm gloves and cap</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Toothbrush and toothpaste</li>
                        <li>Soap and shampoo</li>
                        <li>Sunblock lotion</li>
                        <li>Lip balm</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Water bottle (2 liters)</li>
                        <li>Energy bars and dry fruits</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li>Personal medications</li>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Power bank</li>
                        <li>Cash for personal expenses</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Pick and Drop Transfers from/to Dehradun</li>
                    <li>All meals are included with proper hygiene parameters</li>
                    <li>Triple/double sharing tent accommodations are available</li>
                    <li>During the trek, there will be morning and evening tea with a small refreshment</li>
                    <li>All tolls and forest fees</li>
                    <li>Trekking gears (tents, sleeping bags, ropes, gaiters, crampons etc.)</li>
                    <li>First Aid Medical Kit</li>
                    <li>Expert Guide</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Food during pick and drop transfers</li>
                    <li>Porters/mules charges</li>
                    <li>Personal expenses</li>
                    <li>Insurance</li>
                    <li>Anything apart from the inclusions</li>
                    <li>GST 5%</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>11,675 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>47 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>March to June, September to November</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Dehradun)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 7,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 8,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 8,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Dehradun:</strong> Between 6:00 AM to 8:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Ancient valley trek route</li>
                    <li>Cultural village exploration</li>
                    <li>Stunning mountain views</li>
                    <li>Alpine meadows and forests</li>
                    <li>Traditional architecture</li>
                    <li>Wildlife spotting opportunities</li>
                    <li>Photography paradise</li>
                    <li>Expert guide with local knowledge</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default HarKiDun;
